import { ACTOR_ID_HEADER_KEY, CARE_SERVICE_BASE_URL, SITE_WORKSPACE_ID } from "constants";
import apiClient from "utils/apiClient";
import { bearerGuestToken, ensureGuestSession } from "utils/guestSession";

const BASE = `${CARE_SERVICE_BASE_URL}/api/v1`;
const USE_LOCAL_CARE_PROXY = CARE_SERVICE_BASE_URL.startsWith("/");

const localCareProxyOrigin = () => {
  if (!USE_LOCAL_CARE_PROXY) return undefined;
  return typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";
};

const careRequestConfig = (config = {}) => {
  if (!USE_LOCAL_CARE_PROXY) return config;
  return {
    ...config,
    baseURL: localCareProxyOrigin(),
  };
};

const unwrap = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data !== undefined) return payload.data;
  if (payload?.result !== undefined) return payload.result;
  return payload;
};

const list = (path, params) => apiClient.get(`${BASE}${path}`, careRequestConfig({ params })).then(unwrap);
const get = (path) => apiClient.get(`${BASE}${path}`, careRequestConfig()).then(unwrap);
const post = (path, data) => apiClient.post(`${BASE}${path}`, data, careRequestConfig()).then(unwrap);
const put = (path, data) => apiClient.put(`${BASE}${path}`, data, careRequestConfig()).then(unwrap);
const patch = (path, data) => apiClient.patch(`${BASE}${path}`, data, careRequestConfig()).then(unwrap);
const remove = (path) => apiClient.delete(`${BASE}${path}`, careRequestConfig()).then(unwrap);
const actorConfig = (actorId, config = {}) =>
  careRequestConfig({
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {}),
    },
  });
const requiredId = (value, label) => {
  if (value === undefined || value === null || value === "") {
    throw new Error(`${label} is required.`);
  }
  return encodeURIComponent(value);
};
const closedAssessmentProcessStatuses = new Set(["ARCHIVED", "COMPLETED", "COMPLETE", "DECLINED", "CANCELED", "CANCELLED", "DELETED"]);
const assessmentProcessStatus = (process = {}) =>
  String(process.workflowStatus || process.processStatus || process.status || "").trim().toUpperCase();
const assessmentProcessUpdatedAt = (process = {}) => {
  const date = new Date(process.updatedAt || process.createdAt || process.processStartDate || process.startDate || 0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const activeAssessmentProcessFromList = (processes = []) =>
  (Array.isArray(processes) ? processes : [])
    .filter((process) => {
      const status = assessmentProcessStatus(process);
      return process && (!status || !closedAssessmentProcessStatuses.has(status));
    })
    .sort((a, b) => assessmentProcessUpdatedAt(b) - assessmentProcessUpdatedAt(a))[0] || null;
const createAssessmentProcess = async (data = {}) => {
  if (data?.vehicleId) {
    const existingProcesses = await list("/assessment-processes", { vehicleId: data.vehicleId });
    const activeProcess = activeAssessmentProcessFromList(existingProcesses);
    if (activeProcess) return activeProcess;
  }
  return post("/assessment-processes", data);
};

export const careService = {
  dashboard: (params = {}, actorId) => apiClient.get(`${BASE}/dashboard`, actorConfig(actorId, { params })).then(unwrap),
  reports: {
    overview: (params = {}, actorId) => apiClient.get(`${BASE}/reports/overview`, actorConfig(actorId, { params })).then(unwrap),
  },
  siteProfile: {
    get: (params = {}, actorId) =>
      apiClient.get(`${BASE}/site-profile`, actorConfig(actorId, { params })).then(unwrap),
    getGuest: async () => {
      const guestSession = await ensureGuestSession();
      return apiClient
        .get(
          `${BASE}/site-profile/guest`,
          careRequestConfig({
            params: SITE_WORKSPACE_ID ? { workspaceId: SITE_WORKSPACE_ID, wid: SITE_WORKSPACE_ID } : {},
            skipAuthContext: true,
            headers: {
              Authorization: bearerGuestToken(guestSession),
              "O-SKIP-WORKSPACE-CONTEXT": "1",
            },
          }),
        )
        .then(unwrap);
    },
    save: (data = {}, { actorId, workspaceId, wid } = {}) =>
      apiClient
        .put(
          `${BASE}/site-profile`,
          data,
          actorConfig(actorId, { params: { workspaceId, wid } }),
        )
        .then(unwrap),
  },

  vehicles: {
    list: (params = {}) => list("/vehicles", { includePhotos: false, ...params }),
    listPage: (params = {}) =>
      apiClient
        .get(
          `${BASE}/vehicles`,
          careRequestConfig({ params: { includePhotos: false, ...params } }),
        )
        .then((response) => response?.data),
    listPublic: async (params) => {
      const guestSession = await ensureGuestSession();
      return apiClient
        .get(
          `${BASE}/vehicles`,
          careRequestConfig({
            params: { includePhotos: false, ...(params || {}) },
            skipAuthContext: true,
            headers: {
              Authorization: bearerGuestToken(guestSession),
              "O-SKIP-WORKSPACE-CONTEXT": "1",
            },
          }),
        )
        .then(unwrap);
    },
    listPublicPage: async (params) => {
      const guestSession = await ensureGuestSession();
      return apiClient
        .get(
          `${BASE}/vehicles`,
          careRequestConfig({
            params: { includePhotos: false, ...(params || {}) },
            skipAuthContext: true,
            headers: {
              Authorization: bearerGuestToken(guestSession),
              "O-SKIP-WORKSPACE-CONTEXT": "1",
            },
          }),
        )
        .then((response) => response?.data);
    },
    get: (vehicleId) =>
      apiClient
        .get(
          `${BASE}/vehicles/${requiredId(vehicleId, "Vehicle id")}`,
          careRequestConfig({ params: { includePhotos: true } }),
        )
        .then(unwrap),
    getPublic: async (vehicleId) => {
      const guestSession = await ensureGuestSession();
      return apiClient
        .get(
          `${BASE}/vehicles/${requiredId(vehicleId, "Vehicle id")}`,
          careRequestConfig({
            params: { includePhotos: true },
            skipAuthContext: true,
            headers: {
              Authorization: bearerGuestToken(guestSession),
              "O-SKIP-WORKSPACE-CONTEXT": "1",
            },
          }),
        )
        .then(unwrap);
    },
    createInquiryPublic: async (vehicleId, data = {}) => {
      const guestSession = await ensureGuestSession();
      return apiClient
        .post(
          `${BASE}/customer-inquiries`,
          {
            ...data,
            vehicleId: data.vehicleId || vehicleId,
          },
          careRequestConfig({
            skipAuthContext: true,
            headers: {
              Authorization: bearerGuestToken(guestSession),
              "O-SKIP-WORKSPACE-CONTEXT": "1",
            },
          }),
        )
        .then(unwrap);
    },
    create: (data, { actorId, workspaceId, wid } = {}) =>
      apiClient
        .post(
          `${BASE}/vehicles`,
          data,
          actorConfig(actorId, { params: { workspaceId, wid } }),
        )
        .then(unwrap),
    update: (vehicleId, data, { actorId, workspaceId, wid } = {}) =>
      apiClient
        .put(
          `${BASE}/vehicles/${requiredId(vehicleId, "Vehicle id")}`,
          data,
          actorConfig(actorId, { params: { workspaceId, wid } }),
        )
        .then(unwrap),
    patch: (vehicleId, data, { actorId, workspaceId, wid } = {}) =>
      apiClient
        .patch(
          `${BASE}/vehicles/${requiredId(vehicleId, "Vehicle id")}`,
          data,
          actorConfig(actorId, { params: { workspaceId, wid } }),
        )
        .then(unwrap),
    delete: (vehicleId) => remove(`/vehicles/${requiredId(vehicleId, "Vehicle id")}`),
  },
  customerInquiries: {
    list: (params) => list("/customer-inquiries", params),
    appointments: (params) => list("/customer-inquiries/appointments", params),
    get: (inquiryId) => get(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}`),
    create: (data) => post("/customer-inquiries", data),
    update: (inquiryId, data) => put(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}`, data),
    patch: (inquiryId, data) => patch(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}`, data),
    delete: (inquiryId) => remove(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}`),
    updateStatus: (inquiryId, status, data = {}) =>
      patch(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}/status`, {
        ...data,
        status,
      }),
    markRead: (inquiryId) => patch(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}/read`, {}),
    archive: (inquiryId) => patch(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}/archive`, {}),
    addNote: (inquiryId, data) =>
      post(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}/notes`, data),
    updateNote: (inquiryId, noteId, data) =>
      put(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}/notes/${requiredId(noteId, "Inquiry note id")}`, data),
    deleteNote: (inquiryId, noteId) =>
      remove(`/customer-inquiries/${requiredId(inquiryId, "Customer inquiry id")}/notes/${requiredId(noteId, "Inquiry note id")}`),
  },
  appointments: {
    list: (params) => list("/appointments", params),
    get: (appointmentId) => get(`/appointments/${requiredId(appointmentId, "Appointment id")}`),
    create: (data) => post("/appointments", data),
    update: (appointmentId, data) => put(`/appointments/${requiredId(appointmentId, "Appointment id")}`, data),
    delete: (appointmentId) => remove(`/appointments/${requiredId(appointmentId, "Appointment id")}`),
  },
  assessmentProcesses: {
    list: (params) => list("/assessment-processes", params),
    get: (assessmentProcessId) => get(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}`),
    create: createAssessmentProcess,
    update: (assessmentProcessId, data) => put(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}`, data),
    updateStatus: (assessmentProcessId, data) => patch(`/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/status`, data),
    delete: (assessmentProcessId) => remove(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}`),
  },

  assessmentRecords: {
    list: (params) => list("/assessment-records", params),
    get: (recordId) => get(`/assessment-records/${encodeURIComponent(recordId)}`),
    byProcessAndType: (assessmentProcessId, recordType) =>
      get(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}`),
    saveForProcess: (assessmentProcessId, recordType, data) =>
      put(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}`, data),
    submitDailyNoteForProcess: (assessmentProcessId, recordType, data) =>
      post(`/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/records/${requiredId(recordType, "Record type")}/daily-notes`, data),
    deleteDailyNoteForProcess: (assessmentProcessId, recordType, noteId) =>
      remove(`/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/records/${requiredId(recordType, "Record type")}/daily-notes/${requiredId(noteId, "Daily note id")}`),
    savePagesForProcess: (assessmentProcessId, recordType, data) =>
      put(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}/pages`, data),
  },

  meetAndGreet: {
    getForProcess: (assessmentProcessId) =>
      get(`/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/meet-and-greet`),
    saveForProcess: (assessmentProcessId, data) =>
      put(`/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/meet-and-greet`, data),
  },

  providerDecision: {
    saveForProcess: (assessmentProcessId, data) =>
      put(`/assessment-processes/${requiredId(assessmentProcessId, "Assessment process id")}/provider-decision`, data),
  },

  negotiatedCarePlans: {
    list: (params = {}, actorId) => apiClient.get(`${BASE}/negotiated-care-plans`, actorConfig(actorId, { params })).then(unwrap),
    get: (planId) => get(`/negotiated-care-plans/${encodeURIComponent(planId)}`),
    getForProcess: (assessmentProcessId) =>
      get(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan`),
    saveForProcess: (assessmentProcessId, data) =>
      put(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan`, data),
    generate: (assessmentProcessId, data) =>
      post(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/generate`, data),
    refine: (assessmentProcessId, data) =>
      post(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/refine`, data),
    format: (assessmentProcessId, data) =>
      post(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/format`, data),
  },

  dailyNotes: {
    list: (params) => list("/daily-notes", params),
    create: (data) => post("/daily-notes", data),
    update: (noteId, data) => put(`/daily-notes/${encodeURIComponent(noteId)}`, data),
    delete: (noteId) => remove(`/daily-notes/${encodeURIComponent(noteId)}`),
  },

  careStaffAssignments: {
    list: (params) => list("/care-staff-assignments", params),
    create: (data) => post("/care-staff-assignments", data),
    update: (assignmentId, data) => put(`/care-staff-assignments/${encodeURIComponent(assignmentId)}`, data),
    delete: (assignmentId) => remove(`/care-staff-assignments/${encodeURIComponent(assignmentId)}`),
  },

  careProfiles: {
    list: (params) => list("/care-profiles", params),
    get: (careProfileId) => get(`/care-profiles/${requiredId(careProfileId, "CareProfile id")}`),
    getMine: ({ actorId, ...params } = {}) =>
      apiClient.get(`${BASE}/care-profiles/me`, actorConfig(actorId, { params })).then(unwrap),
    create: ({ actorId, ...data } = {}) =>
      apiClient.post(`${BASE}/care-profiles`, data, actorConfig(actorId)).then(unwrap),
    update: (careProfileId, data) =>
      put(`/care-profiles/${requiredId(careProfileId, "CareProfile id")}`, data),
    patchProperties: (careProfileId, updates, { actorId } = {}) =>
      apiClient
        .patch(
          `${BASE}/care-profiles/${requiredId(careProfileId, "CareProfile id")}/properties`,
          updates,
          actorConfig(actorId),
        )
        .then(unwrap),
    saveStep: (careProfileId, stepKey, { actorId, ...data } = {}) =>
      apiClient
        .put(
          `${BASE}/care-profiles/${requiredId(careProfileId, "CareProfile id")}/onboarding/${requiredId(stepKey, "Onboarding step")}`,
          data,
          actorConfig(actorId),
        )
        .then(unwrap),
  },

  timesheets: {
    listForCareProfile: (careProfileId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .get(
          `${BASE}/timesheets`,
          actorConfig(actorId, { params: { ...(queryParams || {}), careProfileId: requiredId(careProfileId, "CareProfile id") } }),
        )
        .then(unwrap);
    },
    createForCareProfile: (careProfileId, { actorId, ...data } = {}, params = {}) =>
      apiClient
        .post(
          `${BASE}/timesheets`,
          { ...(data || {}), careProfileId: requiredId(careProfileId, "CareProfile id") },
          actorConfig(actorId, { params }),
        )
        .then(unwrap),
    list: (params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient.get(`${BASE}/timesheets`, actorConfig(actorId, { params: queryParams })).then(unwrap);
    },
    create: ({ actorId, ...data } = {}, params = {}) =>
      apiClient.post(`${BASE}/timesheets`, data, actorConfig(actorId, { params })).then(unwrap),
    update: (timesheetId, data, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .put(
          `${BASE}/timesheets/${requiredId(timesheetId, "Timesheet id")}`,
          data,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
    delete: (timesheetId, params = {}) => {
      const { actorId, ...queryParams } = params || {};
      return apiClient
        .delete(
          `${BASE}/timesheets/${requiredId(timesheetId, "Timesheet id")}`,
          actorConfig(actorId, { params: queryParams }),
        )
        .then(unwrap);
    },
  },

  attendance: {
    listForCareProfile: (careProfileId, params) =>
      list("/attendance", { ...(params || {}), careProfileId: requiredId(careProfileId, "CareProfile id") }),
    currentForCareProfile: (careProfileId, params) =>
      list("/attendance/current", { ...(params || {}), careProfileId: requiredId(careProfileId, "CareProfile id") }),
    list: (params) => list("/attendance", params),
    get: (attendanceId, params) =>
      apiClient.get(`${BASE}/attendance/${requiredId(attendanceId, "Attendance id")}`, careRequestConfig({ params })).then(unwrap),
    clockIn: (data, params) =>
      apiClient.post(`${BASE}/attendance/clock-in`, data, careRequestConfig({ params })).then(unwrap),
    clockOut: (attendanceId, data, params) =>
      apiClient.put(`${BASE}/attendance/${requiredId(attendanceId, "Attendance id")}/clock-out`, data, careRequestConfig({ params })).then(unwrap),
    reviewLocation: (attendanceId, { actorId, ...data } = {}, params) =>
      apiClient.put(`${BASE}/attendance/${requiredId(attendanceId, "Attendance id")}/location-review`, data, actorConfig(actorId, { params })).then(unwrap),
  },

  schedules: {
    listForCareProfile: (careProfileId, params) =>
      list("/schedules", { ...(params || {}), careProfileId: requiredId(careProfileId, "CareProfile id") }),
    list: (params) => list("/schedules", params),
    create: ({ actorId, ...data } = {}, params) =>
      apiClient.post(`${BASE}/schedules`, data, actorConfig(actorId, { params })).then(unwrap),
    update: (scheduleId, { actorId, ...data } = {}, params) =>
      apiClient.put(`${BASE}/schedules/${requiredId(scheduleId, "Schedule id")}`, data, actorConfig(actorId, { params })).then(unwrap),
    delete: (scheduleId, { actorId, ...params } = {}) =>
      apiClient.delete(`${BASE}/schedules/${requiredId(scheduleId, "Schedule id")}`, actorConfig(actorId, { params })).then(unwrap),
  },

  documents: {
    list: (params) => list("/documents", params),
    get: (documentId) => get(`/documents/${encodeURIComponent(documentId)}`),
    create: (data) => post("/documents", data),
    delete: (documentId) => remove(`/documents/${encodeURIComponent(documentId)}`),
  },

  workspaceDownloads: {
    list: (params) => list("/downloads", params),
    create: (data) => post("/downloads", data),
    createDirectory: (data) => post("/downloads/directories", data),
    update: (downloadId, data) => put(`/downloads/${requiredId(downloadId, "Download id")}`, data),
    delete: (downloadId) => remove(`/downloads/${requiredId(downloadId, "Download id")}`),
  },

  assessmentExtractions: {
    extractForProcess: (assessmentProcessId, pages) => {
      const encodedProcessId = requiredId(assessmentProcessId, "Assessment process id");
      const formData = new FormData();
      pages.forEach((page) => formData.append("pages", page, page.name));
      return apiClient.post(`${BASE}/assessment-processes/${encodedProcessId}/assessment-extractions`, formData, {
        ...careRequestConfig(),
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 30 * 60 * 1000,
      }).then(unwrap);
    },
  },
};

careService.appraisalProcesses = careService.assessmentProcesses;
careService.appraisalRecords = careService.assessmentRecords;
careService.appraisalExtractions = careService.assessmentExtractions;

export default careService;
